<template>
  <div
    class="template-diagnostics-flyout"
    :class="{ 'template-diagnostics-flyout--visible': visible }"
    role="dialog"
    aria-labelledby="template-diagnostics-title"
    :aria-hidden="!visible"
    ref="panelRef"
  >
    <div class="template-diagnostics-flyout__header">
      <h2 id="template-diagnostics-title" class="template-diagnostics-flyout__title">
        <Icon name="warning" :size="14" />
        Template Issues
      </h2>
      <div class="template-diagnostics-flyout__actions">
        <Button
          variant="icon"
          size="small"
          icon="arrow-clockwise"
          :title="'Refresh preview & diagnostics'"
          @click="$emit('refresh')"
        />
        <Button
          variant="icon"
            size="small"
            icon="x"
            :title="'Close diagnostics panel'"
            @click="handleClose"
        />
      </div>
    </div>
    <div class="template-diagnostics-flyout__content">
      <div v-if="errors.length === 0" class="template-diagnostics-flyout__empty">
        <Icon name="check" :size="20" />
        <span>No template resolution errors</span>
      </div>
      <ul v-else class="template-diagnostics-flyout__list">
        <li
          v-for="err in errors"
          :key="err.id"
          class="template-diagnostics-flyout__item"
        >
          <!-- <Icon name="warning" :size="12" /> -->
          <span class="message">{{ err.message }}</span>
        </li>
      </ul>
    </div>
    <div class="template-diagnostics-flyout__footer" v-if="errors.length">
      <span class="summary">{{ errors.length }} error{{ errors.length === 1 ? '' : 's' }}</span>
  <Button variant="secondary" size="small" @click="copyAll" :title="'Copy all errors'">Copy</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import Button from '@/components/atoms/Button.vue'
import Icon from '@/components/atoms/Icon.vue'
import type { } from 'vue'

interface TemplateDiagnosticsFlyoutProps {
  visible: boolean
  errors: { id: string; message: string }[]
}
const props = defineProps<TemplateDiagnosticsFlyoutProps>()
const emit = defineEmits<{ 'close': []; 'refresh': []; }>()

const panelRef = ref<HTMLElement | null>(null)

function handleClose() { emit('close') }

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close')
}

function onClickOutside(e: MouseEvent) {
  if (!panelRef.value) return
  const target = e.target as HTMLElement
  // Ignore clicks on the diagnostics toggle button/status item so it can handle its own toggle logic
  if (target.closest('.diagnostics-status')) return
  if (!panelRef.value.contains(target)) emit('close')
}

function copyAll() {
  try {
    const text = props.errors.map(e => e.message).join('\n')
    navigator.clipboard.writeText(text)
  } catch {}
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener('mousedown', onClickOutside)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('mousedown', onClickOutside)
})
</script>

<style scoped lang="scss">
.template-diagnostics-flyout {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 420px;
  max-height: 60vh;
  background: var(--vscode-sideBar-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
  box-shadow: var(--shadow-lg, 0 6px 14px rgba(0,0,0,0.35));
  display: flex;
  flex-direction: column;
  transform: translateY(8px) scale(.96);
  opacity: 0;
  visibility: hidden;
  transition: all .18s cubic-bezier(.34,1.56,.64,1);
  z-index: 1100;

  &--visible { transform: translateY(0) scale(1); opacity: 1; visibility: visible; }

  &__header { display:flex; align-items:center; justify-content:space-between; padding:8px 10px; border-bottom:1px solid var(--vscode-widget-border); }
  &__title { display:flex; align-items:center; gap:6px; font-size:13px; font-weight:600; margin:0; }
  &__actions { display:flex; align-items:center; gap:4px; }
  &__content { flex:1; overflow:auto; padding:8px 10px; }
  &__empty { display:flex; align-items:center; gap:8px; font-size:12px; opacity:.8; }
  &__list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:6px; }
  &__item { display:flex; align-items:flex-start; gap:6px; font-size:12px; line-height:1.3; }
  &__item .message { flex:1; }
  &__footer { border-top:1px solid var(--vscode-widget-border); padding:6px 10px; display:flex; align-items:center; justify-content:space-between; font-size:11px; }
  .summary { opacity:.8; }
}
</style>
